import { theme as antdTheme } from "antd";

export const THEME_STORAGE_KEY = "aavgo_theme_mode";

export const themeModes = {
    light: "light",
    dark: "dark",
};

const brandTokens = {
    colorPrimary: "#7C3AED",
    colorPrimaryHover: "#8B5CF6",
    colorPrimaryActive: "#6D28D9",
    colorPrimaryLight: "#A87CF3",
    colorPrimaryDark: "#634498",
    colorSuccess: "#14B8A6",
    colorWarning: "#F59E0B",
    colorError: "#F43F5E",
    colorInfo: "#14B8A6",
    colorLink: "#7C3AED",
    colorLinkHover: "#8B5CF6",
};

const sharedTokens = {
    ...brandTokens,
    borderRadius: 10,
    fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 14,
    controlHeight: 36,
};

const palette = {
    dark: {
        colorBgBase: "#0a0f1e",
        colorBgContainer: "#141824",
        colorBgElevated: "#1a2236",
        colorBgLayout: "#0f172a",
        colorBgSpotlight: "#334155",
        colorBgSidebar: "#0a0f1e",
        colorBgTableHeader: "#0d1220",
        colorBgControl: "#131b2e",
        colorBgRowHover: "#1e2a3d",
        colorBgRowAlt: "#111722",
        colorTextBase: "#e2e8f0",
        colorText: "#e2e8f0",
        colorTextSecondary: "#94a3b8",
        colorTextTertiary: "#64748b",
        colorTextQuaternary: "#475569",
        colorBorder: "#1e293b",
        colorBorderSecondary: "#161f30",
        colorSorter: "#2d3f55",
        colorTextOnPrimary: "#ffffff",
        buttonPrimaryShadow: "0 2px 0 rgba(63, 36, 87, 0.5)",
        shadowGlow: "0 0 20px -5px rgba(124, 58, 237, 0.25)",
        shadowCard:
            "0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px -1px rgba(0, 0, 0, 0.3)",
    },
    light: {
        colorBgBase: "#f8fafc",
        colorBgContainer: "#ffffff",
        colorBgElevated: "#f1f5f9",
        colorBgLayout: "#f8fafc",
        colorBgSpotlight: "#e2e8f0",
        colorBgSidebar: "#ffffff",
        colorBgTableHeader: "#f1f5f9",
        colorBgControl: "#ffffff",
        colorBgRowHover: "#f1f5f9",
        colorBgRowAlt: "#f8fafc",
        colorTextBase: "#0f172a",
        colorText: "#0f172a",
        colorTextSecondary: "#475569",
        colorTextTertiary: "#64748b",
        colorTextQuaternary: "#94a3b8",
        colorBorder: "#e2e8f0",
        colorBorderSecondary: "#cbd5e1",
        colorSorter: "#94a3b8",
        colorTextOnPrimary: "#ffffff",
        buttonPrimaryShadow: "0 2px 0 rgba(124, 58, 237, 0.18)",
        shadowGlow: "0 0 20px -5px rgba(124, 58, 237, 0.2)",
        shadowCard:
            "0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px -1px rgba(15, 23, 42, 0.08)",
    },
};

export const getThemePalette = (mode) =>
    palette[mode] || palette[themeModes.dark];

export const getCssVariables = (mode) => {
    const colors = getThemePalette(mode);

    return {
        "--color-bg": colors.colorBgBase,
        "--color-bg-card": colors.colorBgContainer,
        "--color-bg-elevated": colors.colorBgElevated,
        "--color-bg-row-hover": colors.colorBgRowHover,
        "--color-bg-row-alt": colors.colorBgRowAlt,
        "--color-bg-sidebar": colors.colorBgSidebar,
        "--color-bg-table-header": colors.colorBgTableHeader,
        "--color-bg-control": colors.colorBgControl,
        "--color-bg-spotlight": colors.colorBgSpotlight,
        "--color-primary": brandTokens.colorPrimary,
        "--color-primary-hover": brandTokens.colorPrimaryHover,
        "--color-primary-light": `${brandTokens.colorPrimary}1f`,
        "--color-accent": brandTokens.colorSuccess,
        "--color-accent-light": `${brandTokens.colorSuccess}1f`,
        "--color-success": brandTokens.colorSuccess,
        "--color-success-light": `${brandTokens.colorSuccess}1f`,
        "--color-warning": brandTokens.colorWarning,
        "--color-warning-light": `${brandTokens.colorWarning}1f`,
        "--color-error": brandTokens.colorError,
        "--color-error-light": `${brandTokens.colorError}1f`,
        "--color-text": colors.colorText,
        "--color-text-secondary": colors.colorTextSecondary,
        "--color-text-muted": colors.colorTextTertiary,
        "--color-border": colors.colorBorder,
        "--color-border-light": colors.colorBorderSecondary,
        "--color-sorter": colors.colorSorter,
        "--color-text-on-primary": colors.colorTextOnPrimary,
        "--shadow-button-primary": colors.buttonPrimaryShadow,
        "--shadow-glow": colors.shadowGlow,
        "--shadow-card": colors.shadowCard,
        "--gradient-primary": `linear-gradient(135deg, ${brandTokens.colorPrimary}, ${brandTokens.colorSuccess})`,
    };
};

export const getAppTheme = (mode) => {
    const colors = getThemePalette(mode);
    const isDark = mode === themeModes.dark;

    return {
        algorithm: isDark
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
        token: {
            ...sharedTokens,
            ...colors,
        },
        components: {
            Button: {
                colorPrimary: brandTokens.colorPrimary,
                colorPrimaryHover: brandTokens.colorPrimaryHover,
                colorPrimaryActive: brandTokens.colorPrimaryActive,
                borderRadius: 8,
                controlHeight: 36,
            },
            FloatButton: {
                colorPrimary: brandTokens.colorPrimary,
                colorPrimaryHover: brandTokens.colorPrimaryHover,
                colorPrimaryActive: brandTokens.colorPrimaryActive,
                borderRadius: 8,
                controlHeight: 36,
            },
            Table: {
                headerBg: colors.colorBgTableHeader,
                headerColor: colors.colorTextSecondary,
                headerSortActiveBg: colors.colorBgSpotlight,
                headerSortHoverBg: colors.colorBgSpotlight,
                rowHoverBg: colors.colorBgRowHover,
                colorBgContainer: colors.colorBgContainer,
                borderColor: colors.colorBorder,
                headerBorderRadius: 8,
            },
            Card: {
                colorBgContainer: colors.colorBgContainer,
                colorBorderSecondary: colors.colorBorder,
                borderRadius: 12,
            },
            Input: {
                colorBgContainer: colors.colorBgControl,
                colorBorder: colors.colorBorder,
                colorText: colors.colorText,
                colorTextPlaceholder: colors.colorTextTertiary,
                activeBorderColor: brandTokens.colorPrimary,
                hoverBorderColor: brandTokens.colorPrimaryHover,
                borderRadius: 8,
            },
            Select: {
                colorBgContainer: colors.colorBgControl,
                colorBorder: colors.colorBorder,
                colorText: colors.colorText,
                colorTextPlaceholder: colors.colorTextTertiary,
                optionActiveBg: `${brandTokens.colorPrimary}1f`,
                optionSelectedBg: `${brandTokens.colorPrimary}1f`,
                borderRadius: 8,
            },
            DatePicker: {
                colorBgContainer: colors.colorBgControl,
                colorBorder: colors.colorBorder,
                colorText: colors.colorText,
                colorTextPlaceholder: colors.colorTextTertiary,
                activeBorderColor: brandTokens.colorPrimary,
                borderRadius: 8,
            },
            Layout: {
                bodyBg: colors.colorBgLayout,
                headerBg: colors.colorBgLayout,
                siderBg: colors.colorBgSidebar,
                triggerBg: colors.colorBgElevated,
                triggerColor: colors.colorText,
            },
            Menu: {
                itemBg: colors.colorBgSidebar,
                subMenuItemBg: colors.colorBgLayout,
                itemColor: colors.colorTextSecondary,
                itemHoverColor: colors.colorText,
                itemHoverBg: colors.colorBgElevated,
                itemSelectedBg: `${brandTokens.colorPrimary}1f`,
                itemSelectedColor: brandTokens.colorPrimary,
                darkItemBg: colors.colorBgSidebar,
                darkSubMenuItemBg: colors.colorBgLayout,
                darkItemColor: colors.colorTextSecondary,
                darkItemHoverColor: colors.colorText,
                darkItemHoverBg: colors.colorBgElevated,
                darkItemSelectedBg: `${brandTokens.colorPrimary}1f`,
                darkItemSelectedColor: brandTokens.colorPrimary,
                itemBorderRadius: 8,
            },
            Modal: {
                contentBg: colors.colorBgContainer,
                headerBg: colors.colorBgContainer,
                titleColor: colors.colorText,
            },
            Tabs: {
                inkBarColor: brandTokens.colorPrimary,
                itemActiveColor: brandTokens.colorPrimary,
                itemHoverColor: brandTokens.colorPrimaryHover,
                itemSelectedColor: brandTokens.colorPrimary,
            },
            Tag: {
                borderRadiusSM: 6,
            },
            Badge: {
                colorBgContainer: brandTokens.colorPrimary,
            },
            Pagination: {
                colorPrimary: brandTokens.colorPrimary,
                colorPrimaryHover: brandTokens.colorPrimaryHover,
                itemActiveBg: `${brandTokens.colorPrimary}1f`,
            },
            Tooltip: {
                colorBgSpotlight: colors.colorBgSpotlight,
                colorTextLightSolid: colors.colorText,
            },
            Statistic: {
                titleColor: colors.colorTextSecondary,
                contentColor: colors.colorText,
            },
            Drawer: {
                colorBgElevated: colors.colorBgContainer,
            },
            Popover: {
                colorBgElevated: colors.colorBgContainer,
            },
            Dropdown: {
                colorBgElevated: colors.colorBgContainer,
                controlItemBgHover: `${brandTokens.colorPrimary}1f`,
            },
            Notification: {
                colorBgElevated: colors.colorBgContainer,
                colorText: colors.colorText,
                colorTextLightSolid: colors.colorTextOnPrimary,
                colorError: brandTokens.colorError,
                colorWarning: brandTokens.colorWarning,
                colorSuccess: brandTokens.colorSuccess,
                colorInfo: brandTokens.colorPrimary,
                colorPrimary: brandTokens.colorPrimary,
                borderRadiusLG: 10,
                boxShadowSecondary: colors.shadowCard,
            },
        },
    };
};

export const theme = getAppTheme(themeModes.dark);
